import { Subscription, SubscriptionStatus } from './subscription-model';
import { PhoneNumber } from './phone-number.model';
import { Product } from './product.model';
import {
  VoiceSubscriptionOffering,
  VoiceAllowedChangeInfo,
  VoiceChangeInfo,
  VoiceSubscriptionType,
} from '@sales/modules/subscription/models/offerings-response.model';
import {
  AdditionalServiceVoice,
  AdditionalServiceSet,
  ServiceStatus,
} from '@sales/modules/subscription/models/additional-service-voice.model';
import { Subscriber } from './subscriber.model';
import { Sponsor } from './sponsor.model';
import { SubscriptionHelperService } from '@sales/services/subscription-helper.service';
import {
  AssignedMobilePlanProductModel,
  AssignedProductDetailsModel,
  AssignedServiceProductModel,
  AssignedSubscriptionModel,
} from './assigned-subscription-offering.model';
import { PcmProductCategoryType } from '@app/sales/modules/subscription/models/pcm-product.model';

export class VoiceProduct extends Product {
  offering: VoiceSubscriptionOffering;
  selectedChangeInfo: VoiceChangeInfo;
  subscription: Subscription;
  assignedSubscription: AssignedSubscriptionModel;
  allowedSubscriptionOfferings: VoiceAllowedChangeInfo[];
  isLoadingSubscriptionOfferings: boolean;
  subscriptionServices: AdditionalServiceSet;
  originalSubscriptionServices: AdditionalServiceSet;
  isLoadingSubscriptionDetails: boolean;
  additionalServices: AdditionalServiceVoice[];
  phoneNumber: PhoneNumber;
  mobileProductId: string;
  accessoryProductId: string;
  isAdditionalServiceCampaign: boolean;
  replaceVoiceSimIcc: string;
  replaceVoiceESim: boolean;
  replaceDataSimIcc: string;
  replaceTwinSimIcc: string;
  replaceAdditionalSimANumber: string;
  imei: string;
  sponsor: Sponsor;
  whitelistEntries: string[];

  constructor() {
    super();
    this.offering = new VoiceSubscriptionOffering();
    this.subscription = new Subscription();
    this.subscriptionServices = null;
    this.isLoadingSubscriptionDetails = true;
    this.isLoadingExistingSubscriber = true;
    this.additionalServices = [];
    this.phoneNumber = new PhoneNumber();
  }

  static isActivationInProgress(voiceProduct: VoiceProduct): boolean {
    return (
      new Date(voiceProduct.subscription.activeSince) > new Date() ||
      voiceProduct.subscription.status === SubscriptionStatus.Assigning
    );
  }

  static hasSubscriptionChanged(voiceProduct: VoiceProduct): boolean {
    return voiceProduct.isExistingSubscription && Boolean(voiceProduct.offering?.id);
  }

  static hasSubscriberChanged(voiceProduct: VoiceProduct): boolean {
    return (
      voiceProduct.isExistingSubscription &&
      voiceProduct.existingSubscriber &&
      Object.keys(voiceProduct.existingSubscriber).some(
        key => voiceProduct.existingSubscriber[key] !== voiceProduct.subscriber[key]
      )
    );
  }

  static addedServices(voiceProduct: VoiceProduct): AdditionalServiceVoice[] {
    if (voiceProduct.isExistingSubscription) {
      return SubscriptionHelperService.getServicesAddedToExistingVoiceProduct(voiceProduct);
    }
    return voiceProduct.additionalServices;
  }

  static removedServices(voiceProduct: VoiceProduct): AdditionalServiceVoice[] {
    if (voiceProduct.isExistingSubscription) {
      return SubscriptionHelperService.getServicesRemovedFromExistingVoiceProduct(voiceProduct);
    }
    return [];
  }

  static continuedServices(voiceProduct: VoiceProduct): AdditionalServiceVoice[] {
    if (voiceProduct.isExistingSubscription) {
      return SubscriptionHelperService.getContinuedServicesOnUpgradedVoiceProduct(voiceProduct);
    }
    return [];
  }

  static vasAddon(voiceProduct: VoiceProduct): AssignedServiceProductModel {
    return voiceProduct?.assignedSubscription?.addons?.find(
      addon => addon.productDetails.pcmProductCategory?.value === PcmProductCategoryType.ValueAddedService
    );
  }
}

export class VoiceProductTestBuilder {
  private _voiceProduct: VoiceProduct = new VoiceProduct();

  public Default() {
    this._voiceProduct.id = '1';
    this._voiceProduct.offering.name = 'Mobil 10 GB';
    this._voiceProduct.offering.id = '132123';
    this._voiceProduct.offering.subscriptionType = VoiceSubscriptionType.Regular;
    this._voiceProduct.phoneNumber.isPortingOrder = false;
    this._voiceProduct.phoneNumber.activationDateTime = new Date();
    this._voiceProduct.phoneNumber.orderSim = true;
    this._voiceProduct.phoneNumber.isESim = false;
    this._voiceProduct.subscriber.name = 'Jan Banan';
    this._voiceProduct.subscriber.birthday = new Date();
    this._voiceProduct.subscriber.address = '1234';
    this._voiceProduct.subscriber.postCode = '1234';
    this._voiceProduct.subscriber.city = 'By';
    return this;
  }

  public WithPorting() {
    this._voiceProduct.phoneNumber.isPortingOrder = true;
    this._voiceProduct.phoneNumber.orderSim = false;
    this._voiceProduct.phoneNumber.icc = '1234';
    this._voiceProduct.phoneNumber.aNumber = '123456789';
    return this;
  }

  public WithAdditionalServiceRequireICC() {
    const additionalService = new AdditionalServiceVoice();
    additionalService.requireICC = true;
    additionalService.icc = '123';

    this._voiceProduct.additionalServices = [additionalService];
    return this;
  }

  public WithExistingWithNewOfferingAndChangedSubscriber() {
    this._voiceProduct.isExistingSubscription = true;

    const existingSubscriber = new Subscriber();
    existingSubscriber.birthday = new Date();
    existingSubscriber.address = '1234';
    existingSubscriber.city = 'By';
    existingSubscriber.name = 'new Test name';
    existingSubscriber.postCode = '9998';
    this._voiceProduct.existingSubscriber = existingSubscriber;

    const assignedSubscription = new AssignedSubscriptionModel();
    assignedSubscription.mobilePlan = new AssignedMobilePlanProductModel();
    assignedSubscription.mobilePlan.productDetails = new AssignedProductDetailsModel();
    assignedSubscription.mobilePlan.productDetails.name = 'Mobil 40 GB';
    assignedSubscription.mobilePlan.productDetails.productNo = '2012541';

    return this;
  }

  public WithExistingWithChangedSubscriberAndChangedServices() {
    this._voiceProduct.isExistingSubscription = true;

    this._voiceProduct.offering = null;
    const existingSubscriber = new Subscriber();
    existingSubscriber.birthday = new Date();
    existingSubscriber.address = '1234';
    existingSubscriber.city = 'By';
    existingSubscriber.name = 'new Test name';
    existingSubscriber.postCode = '9998';
    this._voiceProduct.existingSubscriber = existingSubscriber;

    this._voiceProduct.subscriptionServices = new AdditionalServiceSet();
    this._voiceProduct.subscriptionServices.mutexServiceSets = [];
    this._voiceProduct.subscriptionServices.serviceProducts = [
      {
        productId: '1122',
        serviceStatus: ServiceStatus.Inactive,
      } as AdditionalServiceVoice,
    ];
    this._voiceProduct.originalSubscriptionServices = this._voiceProduct.subscriptionServices;

    this._voiceProduct.additionalServices = [
      {
        productId: '1122',
      } as AdditionalServiceVoice,
    ];

    return this;
  }

  public Build() {
    return this._voiceProduct;
  }
}
